/**
 * Redux async actions (src/store/actions/slotActions.js).
 *
 * This module defines Redux Toolkit thunks (createAsyncThunk) that call backend endpoints via the shared axios client.
 * Slices consume the fulfilled payloads in `extraReducers` to update the UI state.
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../helpers/axiosconfig";
import { t } from "../../utils/i18nTranslate";

/**
 * Teacher Availability Management
 */
/**
 * Async thunk: createTeacherAvailability.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const createTeacherAvailability = createAsyncThunk(
  "slots/createTeacherAvailability",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`slots/availability`, formData);
      return response.data;
    } catch (error) {
      console.error("Error creating availability:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || t("store.slotActions.createAvailabilityFailed"));
    }
  }
);

/**
 * Async thunk: getTeacherAvailability.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getTeacherAvailability = createAsyncThunk(
  "slots/getTeacherAvailability",
  async (params, { rejectWithValue }) => {
    try {
      const { teacherId, startDate, endDate, timezone, status, packageId } = params;
      const response = await axios.get(`slots/availability/${teacherId}`, {
        params: {
          startDate,
          endDate,
          timezone,
          status,
          packageId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching teacher availability:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || t("store.slotActions.fetchAvailabilityFailed"));
    }
  }
);

/**
 * Booking Management
 */
/**
 * Async thunk: bookSlot.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const bookSlot = createAsyncThunk(
  "slots/bookSlot",
  async (bookingData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`slots/book`, bookingData);
      return response.data;
    } catch (error) {
      console.error("Error booking slot:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || t("store.slotActions.bookSlotFailed"));
    }
  }
);

/**
 * Async thunk: cancelBooking.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const cancelBooking = createAsyncThunk(
  "slots/cancelBooking",
  async ({ bookingId, cancelledBy, refundWallet, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`v1/bookings/${bookingId}/cancel`, { reason });
      return response.data;
    } catch (error) {
      console.error("Error cancelling booking:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || t("store.slotActions.cancelBookingFailed"));
    }
  }
);

/**
 * Async thunk: rescheduleBooking.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const rescheduleBooking = createAsyncThunk(
  "slots/rescheduleBooking",
  async ({ bookingId, newSlotId, rescheduledBy, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`slots/reschedule`, {
        bookingId,
        newSlotId,
        rescheduledBy,
        reason
      });
      return response.data;
    } catch (error) {
      console.error("Error rescheduling booking:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || t("store.slotActions.rescheduleBookingFailed"));
    }
  }
);

/**
 * Async thunk: completeBooking.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const completeBooking = createAsyncThunk(
  "slots/completeBooking",
  async ({ bookingId, completedBy, teacherNotes }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`v1/bookings/${bookingId}/complete`, {
        notes: teacherNotes,
      });
      return response.data;
    } catch (error) {
      console.error("Error completing booking:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || t("store.slotActions.completeBookingFailed"));
    }
  }
);

/**
 * Async thunk: updateAttendance.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const updateAttendance = createAsyncThunk(
  "slots/updateAttendance",
  async ({ bookingId, attendeeType, status }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`v1/bookings/${bookingId}/attendance`, {
        userType: attendeeType,
        joined: status
      });
      return response.data;
    } catch (error) {
      console.error("Error updating attendance:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || t("store.slotActions.updateAttendanceFailed"));
    }
  }
);

/**
 * Booking Retrieval
 */
/**
 * Async thunk: getUpcomingBookings.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getUpcomingBookings = createAsyncThunk(
  "slots/getUpcomingBookings",
  async (params, { rejectWithValue }) => {
    try {
      const { userId, userType, limit } = params;
      const response = await axios.get(`v1/bookings/upcoming/${userType}/${userId}`, {
        params: {
          limit,
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching upcoming bookings:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || t("store.slotActions.fetchUpcomingFailed"));
    }
  }
);

/**
 * Async thunk: getBookingHistory.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getBookingHistory = createAsyncThunk(
  "slots/getBookingHistory",
  async (params, { rejectWithValue }) => {
    try {
      const { userId, userType, startDate, endDate, status, limit, skip } = params;
      const response = await axios.get(`v1/bookings`, {
        params: {
          ...(userType === 'teacher' ? { teacherId: userId } : { studentId: userId }),
          startDate,
          endDate,
          status,
          limit,
          page: skip ? Math.floor(skip / (limit || 20)) + 1 : 1,
          isPast: true,
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching booking history:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || t("store.slotActions.fetchHistoryFailed"));
    }
  }
);

/**
 * Async thunk: getBookingDetails.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getBookingDetails = createAsyncThunk(
  "slots/getBookingDetails",
  async ({ bookingId, timezone }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`v1/bookings/${bookingId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching booking details:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || t("store.slotActions.fetchDetailsFailed"));
    }
  }
);

/**
 * Async thunk: searchBookings.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const searchBookings = createAsyncThunk(
  "slots/searchBookings",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get(`slots/search`, {
        params: params
      });
      return response.data;
    } catch (error) {
      console.error("Error searching bookings:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || t("store.slotActions.searchBookingsFailed"));
    }
  }
);

/**
 * Admin Functionality
 */
/**
 * Async thunk: migrateOldBookings.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const migrateOldBookings = createAsyncThunk(
  "slots/migrateOldBookings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post(`slots/migrate`);
      return response.data;
    } catch (error) {
      console.error("Error migrating bookings:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || t("store.slotActions.migrateBookingsFailed"));
    }
  }
);

/**
 * Session Management
 */
/**
 * Async thunk: teacherJoinSession.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const teacherJoinSession = createAsyncThunk(
  "slots/teacherJoinSession",
  async ({ bookingId, teacherId, launchOnly = false }, { rejectWithValue }) => {
    try {
      const joinUrl = `teacher-join-session/${bookingId}/${teacherId}`;
      const response = launchOnly === true
        ? await axios.post(joinUrl, { launchOnly: true })
        : await axios.post(joinUrl);
      return response.data;
    } catch (error) {
      console.error("Error joining session:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || t("store.slotActions.joinSessionFailed"));
    }
  }
);

/**
 * Async thunk: studentJoinSession.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const studentJoinSession = createAsyncThunk(
  "slots/studentJoinSession",
  async ({ bookingId, studentId, launchOnly = false }, { rejectWithValue }) => {
    try {
      const joinUrl = `student-join-session/${bookingId}/${studentId}`;
      const response = launchOnly === true
        ? await axios.post(joinUrl, { launchOnly: true })
        : await axios.post(joinUrl);
      return response.data;
    } catch (error) {
      console.error("Error joining session:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || t("store.slotActions.joinSessionFailed"));
    }
  }
);
